/**
 * Where a staged-product field value came from, for scrape-review badges.
 * Newest explicit write wins; otherwise infer from fill/remediation/AI drafts.
 */

import { brandCatalogIdentity } from './brandCatalogIdentity';
import { isUpcBasedDerivedField } from './identifierReviewNotes';

export type StagedValueSource = 'scraped' | 'official' | '3p' | 'ai' | 'manual' | 'motzu' | 'missing';

export interface StagedFieldSource {
  source: StagedValueSource;
  /** 0–100 when the writer was AI and we stored a score. */
  confidence?: number;
  detail?: string;
}

export interface StagedFieldSourceProduct {
  sku?: string;
  mpn?: string;
  upc?: string;
  ean?: string;
  jan?: string;
  gtin?: string;
  countryOfOrigin?: string;
  editHistory?: Array<{ source?: string; fields?: string[] }>;
  enrichment?: { filledFrom?: Array<{ source?: string; fields?: string[] }> };
  /** Ingest time (ISO string, Date or Firestore `{ _seconds }`). */
  createdAt?: unknown;
  ingestedAt?: unknown;
  enrichmentSource?: string;
  enrichmentConfidence?: number;
  identifierCandidates?: Array<{
    upc?: string;
    ean?: string;
    mpn?: string;
    gtin?: string;
    source?: string;
    confidence?: number;
  }>;
  remediation?: {
    gaps?: Record<string, { resolvedBy?: string | null }>;
  };
  descriptionSource?: 'scraped' | 'ai_generated' | 'ai_enhanced' | string;
  aiContentConfidence?: {
    description?: number;
    shortDescription?: number;
    features?: number;
    inTheBox?: number;
    weight?: number;
  };
  weightEvidence?: {
    method?: string;
    confidence?: number;
    quote?: string;
    sourceField?: string;
  };
  tags?: string[];
  verification?: {
    scores?: { categoryAccuracy?: number };
    suggestions?: { suggestedTags?: string[] };
    modelUsed?: string;
  };
  brandId?: string;
  brand?: string;
  variants?: Array<{ source?: string }>;
  source?: { connectorId?: string };
  pricing?: {
    msrpSource?: string;
    salePriceSource?: 'scrape' | 'map_rebate' | 'manual' | string;
  };
}

const LABEL_KEYS: Record<string, string[]> = {
  SKU: ['sku'],
  MPN: ['mpn'],
  UPC: ['upc'],
  EAN: ['ean'],
  GTIN: ['gtin'],
  JAN: ['jan'],
  'Country of Origin': ['countryOfOrigin'],
  Brand: ['brand'],
  Model: ['model'],
  Category: ['category'],
  Type: ['productType'],
  'Type (expanded)': ['productTypeExpanded'],
  Condition: ['condition'],
  'Detected Status': ['detectedStatus', 'status'],
  ASIN: ['asin'],
  'Legacy launchDate': ['launchDate'],
  MSRP: ['pricing.msrp', 'msrp'],
  Retail: ['pricing.retail', 'retail'],
  Sale: ['pricing.salePrice', 'salePrice'],
  Description: ['description'],
  'Short Description': ['shortDescription'],
  Features: ['features'],
  Highlights: ['highlights'],
  'In The Box': ['inTheBox'],
  Weight: ['weight'],
  'Shipping Weight': ['shippingWeight', 'weight'],
  Name: ['name'],
  Title: ['name'],
  Documentation: ['documentation'],
  Videos: ['videos'],
  Tags: ['tags'],
  Images: ['images', 'photo'],
  Specifications: ['specifications', 'specificationsRaw'],
};

const THIRD_PARTY_RE = /bh|bhphoto|adorama|bedford|upcitemdb|barcodelookup|3p/i;
const AI_RE = /gemini|ai_|accept_all_ai|ai_suggestion|ai_fix|ai_generate|identifier_enrichment|ai_classify/i;
const MANUAL_RE = /admin_edit|manual/i;

export function stagedFieldSourcePercent(value?: number): number | undefined {
  if (value == null || !Number.isFinite(value)) return undefined;
  const pct = value <= 1 ? Math.round(value * 100) : Math.round(value);
  return pct >= 0 && pct <= 100 ? pct : undefined;
}

function classifyWriter(raw?: string): StagedValueSource | undefined {
  const value = String(raw || '').trim();
  if (!value) return undefined;
  if (MANUAL_RE.test(value)) return 'manual';
  if (THIRD_PARTY_RE.test(value)) return '3p';
  if (AI_RE.test(value) || value === 'ai_generated' || value === 'ai_enhanced') return 'ai';
  if (/warehouse|promaster|pricing_import|map_ingest|map_rebate|vendor_api|official|\bmap\b/i.test(value)) {
    return 'official';
  }
  if (value === 'scraped' || value === 'vendor' || /scrape|snapshot_parse|content_rescrape|extract_weight/i.test(value)) {
    return 'scraped';
  }
  return undefined;
}

function catalogIngestIsOfficialApi(product: StagedFieldSourceProduct): boolean {
  const catalog = brandCatalogIdentity(product.brandId, product.brand);
  if (!catalog) return false;
  const src = catalog.source.toLowerCase();
  // Storefront JSON is still a scrape, even if the note mentions an API elsewhere.
  if (/shopify|woo|products\.json/.test(src)) return false;
  return /\bapi\b/.test(src);
}

function defaultOrigin(product: StagedFieldSourceProduct): 'scraped' | 'official' {
  const connector = String(product.source?.connectorId || '').toLowerCase();
  const msrpSource = String(product.pricing?.msrpSource || '').toLowerCase();
  if (/pricing_import|map_ingest|\bmap\b|vendor_api|warehouse/.test(msrpSource)) return 'official';
  if ((product.variants || []).some((row) => /\bapi\b|warehouse/i.test(String(row.source || '')))) {
    return 'official';
  }
  // SmallRig / Benro / Promaster persist as `*-scraper` connectors even though
  // identifiers come from the manufacturer API, not a storefront scrape.
  if (catalogIngestIsOfficialApi(product)) return 'official';
  if (connector && /api|ingest|pricing|warehouse|dealer/.test(connector) && !/scraper|shopify|woo/.test(connector)) {
    return 'official';
  }
  return 'scraped';
}

function keysForLabel(label: string): string[] {
  return LABEL_KEYS[label] || [label.replace(/\s+/g, '').replace(/^[A-Z]/, (c) => c.toLowerCase())];
}

function historySource(source?: string): StagedValueSource | undefined {
  if (source === 'admin_edit') return 'manual';
  return classifyWriter(source);
}

function repeatsVendorSku(product: StagedFieldSourceProduct, keys: string[]): boolean {
  const sku = String(product.sku || '').trim();
  if (!sku) return false;
  return keys.some((key) => {
    if (key === 'sku') return false;
    const current = String((product as Record<string, unknown>)[key] || '').trim();
    return !!current && current.toLowerCase() === sku.toLowerCase();
  });
}

function candidateMatch(
  product: StagedFieldSourceProduct,
  keys: string[],
): StagedFieldSource | undefined {
  const candidates = product.identifierCandidates || [];
  for (const key of keys) {
    const current = String((product as Record<string, unknown>)[key] || '').trim();
    if (!current) continue;
    const hit = candidates.find((c) => String((c as Record<string, unknown>)[key] || '').trim() === current);
    if (!hit?.source) continue;
    const source = classifyWriter(hit.source) || (hit.source === 'gemini_ai' ? 'ai' : '3p');
    return {
      source,
      confidence: source === 'ai' ? stagedFieldSourcePercent(hit.confidence) : undefined,
      detail: hit.source,
    };
  }
  return undefined;
}

/**
 * `enrichment.filledFrom` has recorded every identifier enrichment wrote since
 * 2026-09-13 (b7d268ff4). For a product ingested after that, an identifier the
 * record does not list was not written by enrichment: a lookup candidate that
 * matches it only confirms the vendor value (Tamron RMC-RF: the dealer sheet's
 * UPC, found again by Gemini, was badged "AI 95%"). Older products may carry
 * an enrichment write from before the record, so they keep the inference.
 */
const FILLED_FROM_COMPLETE_SINCE_MS = Date.parse('2026-09-14T00:00:00Z');

function timeMillis(value: unknown): number {
  if (!value) return 0;
  if (typeof value === 'object' && !(value instanceof Date)) {
    const v = value as { _seconds?: number; seconds?: number };
    if (v._seconds != null) return v._seconds * 1000;
    if (v.seconds != null) return v.seconds * 1000;
  }
  const t = new Date(value as string | number | Date).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function enrichmentDidNotWrite(product: StagedFieldSourceProduct, keys: string[]): boolean {
  if (!keys.some((k) => k === 'upc' || k === 'ean' || k === 'gtin' || k === 'mpn')) return false;
  const ingested = timeMillis(product.ingestedAt) || timeMillis(product.createdAt);
  if (!ingested || ingested < FILLED_FROM_COMPLETE_SINCE_MS) return false;
  const fills = product.enrichment?.filledFrom || [];
  return !fills.some((fill) => keys.some((key) => (fill.fields || []).includes(key)));
}

/**
 * Resolve the badge for a Product Info / pricing / copy field label.
 * Callers should show `missing` when the field has no value.
 */
export function stagedFieldSource(
  product: StagedFieldSourceProduct | null | undefined,
  label: string,
): StagedFieldSource | undefined {
  if (!product) return undefined;
  const keys = keysForLabel(label);

  const history = product.editHistory || [];
  for (let i = history.length - 1; i >= 0; i--) {
    const entry = history[i];
    const fields = entry.fields || [];
    if (!keys.some((key) => fields.includes(key) || fields.includes(label))) continue;
    const source = historySource(entry.source);
    if (!source) continue;
    const confidence =
      source === 'ai' && (keys.includes('category') || keys.includes('productType'))
        ? stagedFieldSourcePercent(product.verification?.scores?.categoryAccuracy)
        : source === 'ai' && keys.includes('shortDescription')
          ? stagedFieldSourcePercent(product.aiContentConfidence?.shortDescription)
          : source === 'ai' && keys.includes('description')
            ? stagedFieldSourcePercent(product.aiContentConfidence?.description)
            : source === 'ai' && keys.includes('features')
              ? stagedFieldSourcePercent(product.aiContentConfidence?.features)
              : source === 'ai' && keys.includes('inTheBox')
                ? stagedFieldSourcePercent(product.aiContentConfidence?.inTheBox)
                : source === 'ai' && keys.includes('weight')
                  ? stagedFieldSourcePercent(
                      product.weightEvidence?.confidence ?? product.aiContentConfidence?.weight,
                    )
                  : undefined;
    const fromHistory = { source, confidence, detail: entry.source };
    if (source !== 'manual' && keys.includes('ean') && isUpcBasedDerivedField('ean', product)) {
      return { source: 'motzu', detail: 'upc_based' };
    }
    if (source !== 'manual' && keys.includes('gtin') && isUpcBasedDerivedField('gtin', product)) {
      return { source: 'motzu', detail: 'upc_based' };
    }
    return fromHistory;
  }

  if (keys.includes('ean') && isUpcBasedDerivedField('ean', product)) {
    return { source: 'motzu', detail: 'upc_based' };
  }
  if (keys.includes('gtin') && isUpcBasedDerivedField('gtin', product)) {
    return { source: 'motzu', detail: 'upc_based' };
  }

  const fills = product.enrichment?.filledFrom || [];
  for (let i = fills.length - 1; i >= 0; i--) {
    const fill = fills[i];
    if (!keys.some((key) => (fill.fields || []).includes(key))) continue;
    return {
      source: classifyWriter(fill.source) || '3p',
      detail: fill.source,
    };
  }

  // A Gemini/3P candidate that merely *repeats* the live SKU is confirmation,
  // not authorship. Peak Design (and most brands) scrape MPN = SKU; enrichment
  // then stores the same string as a candidate and we used to stamp MPN as AI.
  const notEnriched = enrichmentDidNotWrite(product, keys);
  const fromCandidate = notEnriched ? undefined : candidateMatch(product, keys);
  if (fromCandidate && !repeatsVendorSku(product, keys)) return fromCandidate;

  const gaps = product.remediation?.gaps || {};
  for (const key of keys) {
    const resolvedBy = gaps[key]?.resolvedBy;
    const source = classifyWriter(resolvedBy || '');
    if (!source || repeatsVendorSku(product, [key])) continue;
    const confidence =
      source === 'ai' && (key === 'upc' || key === 'ean' || key === 'mpn' || key === 'gtin')
        ? stagedFieldSourcePercent(product.enrichmentConfidence)
        : undefined;
    return { source, confidence, detail: resolvedBy || undefined };
  }

  if (keys.includes('description') || keys.includes('shortDescription')) {
    const src = classifyWriter(product.descriptionSource);
    if (src && src !== 'scraped' && src !== 'official') {
      const confidence = keys.includes('shortDescription')
        ? stagedFieldSourcePercent(product.aiContentConfidence?.shortDescription)
        : stagedFieldSourcePercent(product.aiContentConfidence?.description);
      return { source: src, confidence, detail: product.descriptionSource };
    }
  }

  if (!notEnriched && (keys.includes('upc') || keys.includes('ean') || keys.includes('mpn') || keys.includes('gtin'))) {
    const src = classifyWriter(product.enrichmentSource);
    if ((src === 'ai' || src === '3p') && !repeatsVendorSku(product, keys)) {
      return {
        source: src,
        confidence: src === 'ai' ? stagedFieldSourcePercent(product.enrichmentConfidence) : undefined,
        detail: product.enrichmentSource,
      };
    }
  }

  if (keys.includes('tags')) {
    return { source: 'motzu', detail: 'taxonomy' };
  }

  if (keys.includes('salePrice') || keys.includes('pricing.salePrice')) {
    const saleSrc = product.pricing?.salePriceSource;
    const classified = classifyWriter(saleSrc);
    if (classified) return { source: classified, detail: saleSrc };
  }

  if (keys.includes('weight') && product.weightEvidence?.method === 'ai') {
    return {
      source: 'ai',
      confidence: stagedFieldSourcePercent(
        product.weightEvidence.confidence ?? product.aiContentConfidence?.weight,
      ),
      detail: product.weightEvidence.sourceField || product.weightEvidence.quote,
    };
  }

  if (keys.includes('msrp') || keys.includes('pricing.msrp') || keys.includes('retail') || keys.includes('pricing.retail')) {
    const msrpSource = String(product.pricing?.msrpSource || '');
    const official = classifyWriter(msrpSource);
    if (official === 'official') return { source: 'official', detail: msrpSource };
  }

  return { source: defaultOrigin(product) };
}

/** Section headers: red Missing when empty, otherwise the usual inference. */
export function stagedSectionSource(
  product: StagedFieldSourceProduct | null | undefined,
  label: string,
  hasValue: boolean,
): StagedFieldSource {
  if (!hasValue) return { source: 'missing' };
  return stagedFieldSource(product, label) || { source: 'missing' };
}

function tagSlugs(values?: unknown): string[] {
  if (!Array.isArray(values)) return [];
  return values.map((value) => String(value || '').trim().toLowerCase()).filter(Boolean);
}

function historyTouchesTags(fields?: string[]): boolean {
  return (fields || []).some((field) => field === 'tags' || field === 'Tags');
}

function liveTagsIncludeAiSuggestion(product: StagedFieldSourceProduct): boolean {
  const live = new Set(tagSlugs(product.tags));
  if (live.size === 0) return false;
  return tagSlugs(product.verification?.suggestions?.suggestedTags).some((slug) => live.has(slug));
}

/**
 * Tags are Motzu vocabulary (rules + catalog slugs), not vendor storefront tags.
 * After AI verify they are usually a Motzu + AI union — show both chips.
 */
export function stagedTagSources(
  product: StagedFieldSourceProduct | null | undefined,
  hasValue: boolean,
): StagedFieldSource[] {
  if (!hasValue) return [{ source: 'missing' }];
  if (!product) return [{ source: 'motzu', detail: 'taxonomy' }];

  const history = product.editHistory || [];
  for (let i = history.length - 1; i >= 0; i--) {
    const entry = history[i];
    if (!historyTouchesTags(entry.fields)) continue;
    const source = historySource(entry.source);
    if (source === 'manual') return [{ source: 'manual', detail: entry.source }];
    if (source === 'ai') {
      return [
        { source: 'motzu', detail: 'taxonomy' },
        { source: 'ai', detail: entry.source },
      ];
    }
    break;
  }

  const sources: StagedFieldSource[] = [{ source: 'motzu', detail: 'taxonomy' }];
  if (liveTagsIncludeAiSuggestion(product)) {
    sources.push({ source: 'ai', detail: 'suggestedTags' });
  }
  return sources;
}
